import React, { useRef, useState, useEffect } from 'react';
import styles from './container.module.scss';
import { IconWrapper } from '../../../Atoms/icons/Icon';
import Modal from '../../../Atoms/Modal/Modal';
import { useFetchActiveNotifications } from '../../../../Hooks/Notification';
import { useFormikContext } from 'formik';
import { useFetchUnpagedESignature } from '../../../../Hooks/ESignature';
import CustomizedSelect from '../../../Atoms/Inputs/CustomizedSelect';
import TinyMCEEditor from '../../../Atoms/Inputs/TinyMCEEditor/TinyMCEEditor';
import EmailInput from './models/EmailInput';
import { imageSrc } from './models/function';

const Body = ({ reply, setReply }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext();

  const fileInputRef = useRef(null);
  const bodyRef = useRef(null);

  const [selectTemplate, setSelectTemplate] = useState(null);
  const [selectSignature, setSelectSignature] = useState(null);
  const { data: eSignature } = useFetchUnpagedESignature();
  const { data: emails } = useFetchActiveNotifications(false, 4);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleAddAttachments = (event) => {
    const files = Array.from(event.target.files);
    const updatedFiles = [...values.attachments, ...files];
    setFieldValue('attachments', updatedFiles);
  };

  const handleRemoveAttachment = (index) => {
    const updated = [...values.attachments];
    updated.splice(index, 1);
    setFieldValue('attachments', updated);
  };

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({
        top: bodyRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [values.attachments.length, values.signatureId, reply?.more]);

  return (
    <>
      <div className={styles.body}>
        <div className={styles.top}>
          <div className={styles.recipients}>
            <EmailInput name='to' label='To' errors={errors.to && touched.to} />
            <EmailInput name='cc' label='Cc' />

            <div className={styles.emails}>
              <span className={styles.label}>Subject:</span>
              <input
                className={`${styles.input} ${
                  errors.subject && errors.subject ? styles.error : ''
                }`}
                name='subject'
                value={values.subject}
                onChange={(e) => setFieldValue('subject', e.target.value)}
                placeholder='Add Subject'
              />
            </div>
          </div>
        </div>

        <div className={styles.editor} ref={bodyRef}>
          <TinyMCEEditor
            name='bodyHtml'
            value={values.bodyHtml}
            onChange={(name, value) => setFieldValue(name, value)}
            border={false}
            minHeight='50px'
          />

          {reply?.more === false && (
            <>
              <hr style={{ marginTop: '10px', marginBottom: '10px' }} />
              <p
                dangerouslySetInnerHTML={{
                  __html: reply?.content?.bodyContent,
                }}
              ></p>
            </>
          )}

          {values.attachments.length > 0 && (
            <div className={styles.attachments_container}>
              <div className={styles.attachments_header}>Attachment</div>
              <div className={styles.attachments_list}>
                {values.attachments.map((file, index) => {
                  const fileType =
                    file?.type?.split('/').pop() ||
                    file?.name?.split('.').pop();
                  return (
                    <div key={index} className={styles.fileItem}>
                      <img
                        src={imageSrc(fileType)}
                        className={styles.image}
                        alt='file'
                      />

                      <div className={styles.fileTitle}>
                        {`${file.name.slice(0, 12)}...`}{' '}
                        <span>({(file.size / 1024).toFixed(1)} KB)</span>
                        <div>
                          <p>View</p>
                          <p onClick={() => handleRemoveAttachment(index)}>
                            Remove
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {reply?.show && reply?.more && (
          <div
            className={styles.more}
            onClick={() =>
              setReply((prev) => ({
                ...prev,
                more: false,
              }))
            }
          >
            <IconWrapper icon='more' className={styles.icon} />
          </div>
        )}

        <div className={styles.buttons_Container}>
          <button
            className={values.attachments?.length > 0 && styles.filled}
            onClick={handleButtonClick}
          >
            <IconWrapper icon='attachment' className={styles.icon} />
            Attach a file
            <input
              type='file'
              hidden
              multiple
              ref={fileInputRef}
              onChange={handleAddAttachments}
            />
          </button>
          <button
            className={values.emailTemplateId && styles.filled}
            onClick={() =>
              setSelectTemplate((prev) => ({ ...prev, show: true }))
            }
          >
            <IconWrapper icon='template' className={styles.icon} />
            Select Template
          </button>
          <button
            className={values.signatureId && styles.filled}
            onClick={() => setSelectSignature((prev) => !prev)}
          >
            <IconWrapper icon='signature' className={styles.icon} />
            Add Signature
          </button>
        </div>
      </div>

      {selectTemplate?.show && (
        <Modal
          width='40%'
          title='Email Template'
          onClose={() => setSelectTemplate(null)}
        >
          <div className={styles.emailsContainer}>
            <div className={styles.emailsList}>
              <CustomizedSelect
                label='Email Template'
                placeholder='Select Email Template'
                options={emails?.data?.map(
                  ({ notificationId, notificationName }) => ({
                    value: notificationId,
                    text: notificationName,
                  })
                )}
                name='emailTemplateId'
                value={values.emailTemplateId}
                onChange={(name, value) => {
                  setFieldValue(name, value);

                  const selectedTemplate = emails?.data?.find(
                    (email) => email.notificationId === value
                  );

                  if (selectedTemplate?.notificationContent) {
                    setFieldValue(
                      'bodyHtml',
                      (prevBody) =>
                        `${prevBody || ''}\n${
                          selectedTemplate.notificationContent
                        }`
                    );
                  }

                  setSelectTemplate(false);
                }}
              />
            </div>
          </div>
        </Modal>
      )}

      {selectSignature && (
        <Modal
          width='40%'
          title='Email Signature'
          onClose={() => setSelectSignature(false)}
        >
          <div className={styles.emailsContainer}>
            <div className={styles.emailsList}>
              <CustomizedSelect
                label='Email Signature'
                placeholder='Select E-signature'
                options={eSignature?.data?.map(
                  ({ emailSignatureId, signatureName }) => ({
                    value: emailSignatureId,
                    text: signatureName,
                  })
                )}
                name='signatureId'
                value={values.signatureId}
                onChange={(name, value) => {
                  setFieldValue(name, value);

                  const selectedSignature = eSignature?.data?.find(
                    (signature) => signature.emailSignatureId === value
                  );

                  if (selectedSignature?.signature) {
                    setFieldValue(
                      'bodyHtml',
                      (prevBody) =>
                        `${prevBody || ''}\n${selectedSignature.signature}`
                    );
                  }

                  setSelectSignature(false);
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Body;
